// src/components/riskassessment/data/crypto-questions.js
//
// ARCrypto Risk Assessment — 15-Question Framework
// Source: ARCrypto_Risk_Assessment_Blueprint.md (April 2026)
// Categories (dimensions, weighted in cryptoCalculationUtils.js):
//   - tolerance  (45%)  psychological comfort with volatility/loss
//   - capacity   (30%)  financial ability to absorb losses
//   - preference (25%)  product-level fit (leverage / style / targets)
//
// Each question is a 1–4 slider (with half-step granularity for UX).
// Score 1 = most conservative, 4 = most aggressive.
// Some questions trigger SAFETY-RAIL OVERRIDES in the scoring engine —
// see cryptoCalculationUtils.js for the full override rules.

const SLIDER_VALUE_MAP = { 1: 1, 1.5: 1.5, 2: 2, 2.5: 2.5, 3: 3, 3.5: 3.5, 4: 4 };

// Helper to keep questions consistent: takes 4 distinct labels/details and
// expands them to the 7-stop slider format (with half-step labels showing
// the bracket between adjacent integers).
const fourStop = (l1, l2, l3, l4, d1, d2, d3, d4) => ({
  labels: [l1, `${l1} → ${l2}`, l2, `${l2} → ${l3}`, l3, `${l3} → ${l4}`, l4],
  details: [d1, d1, d2, d2, d3, d3, d4],
});

const buildQ = (id, category, subCategory, text, four) => ({
  id,
  text,
  type: "slider",
  category,
  subCategory,
  min: 1,
  max: 4,
  labels: four.labels,
  details: four.details,
  valueMap: SLIDER_VALUE_MAP,
});

export const questions = [
  /* ─── DIMENSION 1: RISK TOLERANCE (45% weight) ──────────────────────── */
  buildQ(
    "q1",
    "tolerance",
    "volatility_perception",
    "When you hear \"volatility,\" what's your first thought?",
    fourStop(
      "Stay away",
      "Cautious",
      "See opportunity",
      "Love the chaos",
      "Volatility scares me. I want stability above everything else.",
      "I'm wary of volatility but understand it's part of crypto.",
      "Volatility is where the opportunity lives — I lean into it carefully.",
      "Chaos is where outsized returns are made. I actively seek it."
    )
  ),
  buildQ(
    "q2",
    "tolerance",
    "loss_reaction_30",
    "What would you do if your portfolio dropped 30% in a week?",
    fourStop(
      "Sell everything",
      "Trim & wait",
      "Hold steady",
      "Buy the dip",
      "I would panic-sell to protect what's left.",
      "I would reduce exposure but not exit completely.",
      "I would hold my positions and ride it out.",
      "I would treat it as a buying opportunity and add capital."
    )
  ),
  buildQ(
    "q3",
    "tolerance",
    "ruin_impact", // SAFETY RAIL: 1 or 2 caps profile at Moderate
    "If your entire crypto portfolio went to zero tomorrow, how would that impact your life?",
    fourStop(
      "Devastating",
      "Major hardship",
      "Manageable",
      "No impact",
      "Devastating — I would not financially recover.",
      "Major hardship — I would feel it for years.",
      "Painful but manageable. I'd survive.",
      "Effectively no impact — this capital is truly discretionary."
    )
  ),
  buildQ(
    "q4",
    "tolerance",
    "long_term_uncertainty",
    "How do you feel about long-term uncertainty in your investments?",
    fourStop(
      "Need clarity",
      "Prefer predictability",
      "Comfortable",
      "Embrace it",
      "I need a clear path. Uncertainty is uncomfortable.",
      "I prefer predictable outcomes but can tolerate some unknowns.",
      "I'm comfortable with multi-year uncertainty for the right setups.",
      "I embrace uncertainty. The unknown is where edges form."
    )
  ),
  buildQ(
    "q5",
    "tolerance",
    "loss_reaction_10",
    "If you lost 10% in a single day, what would you do?",
    fourStop(
      "Sell immediately",
      "Reduce exposure",
      "Hold and wait",
      "Add to position",
      "I would sell immediately to stop further losses.",
      "I'd trim the position to reduce stress.",
      "I'd hold and wait — single days don't determine outcomes.",
      "I'd add to my position if my thesis is intact."
    )
  ),
  buildQ(
    "q14",
    "tolerance",
    "worst_60day_crash",
    "Think about the worst 60-day crypto crash you can recall. If that happened to YOUR money, you would:",
    fourStop(
      "Liquidate all",
      "Sell most",
      "Hold through",
      "Buy aggressively",
      "Liquidate everything to preserve capital.",
      "Sell most positions, keep a small core.",
      "Hold through the entire drawdown — these always recover.",
      "Use the crash as my biggest accumulation window."
    )
  ),
  buildQ(
    "q15",
    "tolerance",
    "past_behavior", // SAFETY RAIL: value 1 + score Growth+ flags mismatch
    "When your investments dropped significantly in the past, what did you ACTUALLY do? (Not what you'd plan to do — what you DID.)",
    fourStop(
      "Sold at a loss",
      "Reduced position",
      "Held through it",
      "Added more",
      "I sold at a loss and locked in the damage.",
      "I reduced my position to ease the stress.",
      "I held through the entire move and waited for recovery.",
      "I added more capital when prices were down."
    )
  ),

  /* ─── DIMENSION 2: RISK CAPACITY (30% weight) ────────────────────────── */
  buildQ(
    "q6",
    "capacity",
    "crypto_allocation", // SAFETY RAIL: value 4 + Q9=1 caps at Conservative
    "What portion of your total net worth is allocated to crypto / high-risk assets?",
    fourStop(
      "Less than 10%",
      "10–30%",
      "30–50%",
      "More than 50%",
      "Less than 10%. Crypto is a small slice of my net worth.",
      "Between 10% and 30%. Meaningful but not dominant.",
      "Between 30% and 50%. Crypto is a major part of my portfolio.",
      "More than 50%. I'm heavily concentrated in crypto."
    )
  ),
  buildQ(
    "q7",
    "capacity",
    "time_horizon", // SAFETY RAIL: value 1 caps at Moderate
    "How long is your investment time horizon for this capital?",
    fourStop(
      "Less than 1 year",
      "1–3 years",
      "3–5 years",
      "5+ years",
      "Less than 1 year. I'll need this capital soon.",
      "1–3 years. Medium-term horizon.",
      "3–5 years. I can ride out a full crypto cycle.",
      "5+ years. Very long horizon — multi-cycle thinking."
    )
  ),
  buildQ(
    "q8",
    "capacity",
    "emergency_fund", // SAFETY RAIL: value 1 caps at Conservative
    "Do you have 6+ months of living expenses saved outside of crypto?",
    fourStop(
      "No emergency fund",
      "1–3 months",
      "3–6 months",
      "6+ months",
      "No — I have no meaningful emergency reserves.",
      "1–3 months. A small buffer.",
      "3–6 months. A solid emergency cushion.",
      "6+ months. I'm well-protected against income loss."
    )
  ),
  buildQ(
    "q9",
    "capacity",
    "income_stability", // SAFETY RAIL: value 1 + Q6=4 caps at Conservative
    "Are you currently employed with stable disposable income?",
    fourStop(
      "Unemployed",
      "Part-time / freelance",
      "Stable employment",
      "Multiple income streams",
      "Unemployed or no current income.",
      "Part-time, gig work, or freelance income — not steady.",
      "Stable employment with consistent disposable income.",
      "Multiple stable income streams (W2 + business + investments)."
    )
  ),

  /* ─── DIMENSION 3: PRODUCT PREFERENCE (25% weight) ───────────────────── */
  buildQ(
    "q10",
    "preference",
    "leverage_comfort",
    "What's your comfort level with leverage?",
    fourStop(
      "Never use it",
      "Tolerate small",
      "Comfortable",
      "Embrace high lev",
      "I never use leverage. It's too dangerous.",
      "I can tolerate small leverage (2–3x) on conviction trades.",
      "I'm comfortable with moderate leverage (5–10x) on managed strategies.",
      "I embrace high leverage (10x+) when the setup is right."
    )
  ),
  buildQ(
    "q11",
    "preference",
    "win_reaction",
    "How do you react after a big win (5x return)?",
    fourStop(
      "Take all profits",
      "Trim aggressively",
      "Take some, let ride",
      "Let it ride",
      "I take all profits — never give a winner back.",
      "I trim aggressively to lock in most of the gain.",
      "I take some profits but let the bulk continue running.",
      "I let it ride — winners can become 50x or 100x."
    )
  ),
  buildQ(
    "q12",
    "preference",
    "return_target",
    "What return are you targeting in the next 12 months?",
    fourStop(
      "5–15%",
      "15–40%",
      "40–100%",
      "100%+",
      "5–15%. I want to outpace inflation safely.",
      "15–40%. Steady, above-market growth.",
      "40–100%. Significant outperformance.",
      "100% or more. I'm chasing asymmetric returns."
    )
  ),
  buildQ(
    "q13",
    "preference",
    "trading_style",
    "Do you actively trade or prefer a hands-off approach?",
    fourStop(
      "Fully hands-off",
      "Quarterly review",
      "Monthly active",
      "Daily active",
      "Fully hands-off. I want algorithms to do the work.",
      "I check quarterly and rebalance occasionally.",
      "I'm engaged monthly — adjusting positions as needed.",
      "Daily active trader. I make moves constantly."
    )
  ),
];

export default questions;
